import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuditLogSidebar } from '../../../contexts/audit-log-sidebar.context.tsx';
import { exportChart } from '../../../helpers/image-downloader/image-downloader.ts';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { Button } from '../../../ui-library/button/button.tsx';
import { FloraButton } from '../../flora/flora-button';
import { useWorkPeriodGoals } from '../../../helpers/process-analysis-shared/process-analysis-shared.hooks';
import { WorkPeriodType } from '../work-periods.type';
import type { TaskRecord } from '../../tasks-table/tasks-table.type.ts';
import { NoWorkPeriodSelected } from '../components/no-work-period-selected';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import { formatAssessmentDataForFlora } from '../helpers/single-assessment-flora.helpers';
import { useAssessmentViewData } from '../hooks/work-periods-calculation.hooks';
import { useHasSelection } from '../hooks/work-periods.hooks';
import { GoalsSection, GoalsSectionSkeleton } from './sections/goals-section';
import { AIInsights, HealthScoreSection, HealthScoreSectionSkeleton } from './sections/health-score-section';
import { isHealthScoreSectionEnabled } from './sections/helpers/key-measures-section.helpers.ts';
import { KeyMeasuresSection } from './sections/key-measures-section';

type Props = {
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
  tasksOpen: boolean;
};

/**
 * Top-level Assessment tab for the Work Periods module.
 *
 * Renders the chart header immediately and lets each region (health score,
 * goals, key measures) drive its own skeleton placeholder while its data
 * loads. This replaces the previous full-screen `BrandedLoadingOverlay` that
 * blocked all interaction until every underlying measure query resolved.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The Assessment tab content
 */
export function AssessmentView({ onTasksChange, tasksOpen }: Props) {
  const i18n = useTranslation();
  const downloadRef = useRef<HTMLDivElement>(null!);
  const { toggleAuditLogSidebar } = useAuditLogSidebar();
  const { selectedWorkPeriods, dateRange, selectedWorkPeriodType } = useWorkPeriodStore();
  const selectedWorkPeriod = useMemo(() => selectedWorkPeriods[0], [selectedWorkPeriods]);
  const useAI = useGlobalStore((state) => state.useAI);

  const hasSelection = useHasSelection();

  const { scoreData, keyMeasuresData, calculatedScopeChange, isLoadingScore, isLoadingKeyMeasures } =
    useAssessmentViewData();
  const { data: goals, query: goalsQuery } = useWorkPeriodGoals({ workPeriodId: selectedWorkPeriod?.id });

  const score = scoreData?.health_scores?.score_by_points;
  const showScoreSection = isHealthScoreSectionEnabled(score);

  const floraData = useMemo(
    () =>
      formatAssessmentDataForFlora(
        scoreData,
        keyMeasuresData,
        goals,
        showScoreSection,
        selectedWorkPeriod?.ai_insights,
        selectedWorkPeriod,
      ),
    [scoreData, keyMeasuresData, goals, showScoreSection, selectedWorkPeriod],
  );

  const hasGoals = selectedWorkPeriod && selectedWorkPeriodType === WorkPeriodType.Defined && goals && goals.length;
  const showGoalsSkeleton =
    selectedWorkPeriod && selectedWorkPeriodType === WorkPeriodType.Defined && goalsQuery.isLoading && !goals;

  if (!hasSelection) {
    return <NoWorkPeriodSelected />;
  }

  const handleExport = () => {
    if (!downloadRef.current) return;
    exportChart(downloadRef.current, 'work-phase-distribution.png');
  };

  return (
    <div ref={downloadRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WorkPeriodChartHeader
        title="Assessment"
        selectedWorkPeriod={selectedWorkPeriod}
        onExport={handleExport}
        hideRadio
        additionalControls={
          <Button variant="outline" radius="xl" size="xs" onClick={toggleAuditLogSidebar}>
            Audit Log
          </Button>
        }
        floraButton={
          useAI && (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t('flora.work_periods.assessment_view_prompt')}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      {isLoadingScore ? (
        <HealthScoreSectionSkeleton
          selectedWorkPeriod={selectedWorkPeriod}
          selectedWorkPeriodType={selectedWorkPeriodType}
        />
      ) : showScoreSection ? (
        <HealthScoreSection
          score={score ?? 0}
          selectedWorkPeriod={selectedWorkPeriod}
          selectedWorkPeriodType={selectedWorkPeriodType}
        />
      ) : (
        selectedWorkPeriod?.ai_insights && (
          <AIInsights
            selectedWorkPeriod={selectedWorkPeriod}
            selectedWorkPeriodType={selectedWorkPeriodType}
            addMarginTop={true}
          />
        )
      )}
      {hasGoals ? (
        <GoalsSection goals={goals} goalsQuery={goalsQuery} workPeriodId={selectedWorkPeriod.id} />
      ) : showGoalsSkeleton ? (
        <GoalsSectionSkeleton />
      ) : null}
      <KeyMeasuresSection
        keyMeasuresData={keyMeasuresData}
        dateRange={dateRange}
        selectedWorkPeriod={selectedWorkPeriod}
        onTasksChange={onTasksChange}
        tasksOpen={tasksOpen}
        calculatedScopeChange={calculatedScopeChange}
        isLoading={isLoadingKeyMeasures}
      />
    </div>
  );
}
